(() => {
  class BleusaltProductGallery {
    constructor(root) {
      this.root = root;
      this.view = root.querySelector('[data-bs-gallery-view]');
      this.nav = root.querySelector('[data-bs-thumbnails]');
      this.dialog = root.querySelector('[data-bs-zoom]');
      this.labels = JSON.parse(root.querySelector('[data-bs-gallery-labels]').textContent);
      this.desktop = window.matchMedia('(min-width: 750px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.root.addEventListener('click', event => this.click(event), options);
      // Swiper's legacy focus handler counts hidden variant slides and jumps away
      // from the tapped image. Keep focus on our image button without that jump.
      this.root.addEventListener('focus', event => {
        if (event.target.matches('[data-bs-open]') && event.target.closest('.swiper-wrapper')) event.stopPropagation();
      }, { ...options, capture: true });
      this.nav.addEventListener('keydown', event => this.navigateThumbnails(event), options);
      this.root.querySelectorAll('.product__accordion details').forEach(details => {
        details.addEventListener('toggle', () => {
          if (details.open) this.root.querySelectorAll('.product__accordion details').forEach(other => {
            if (other !== details) other.open = false;
          });
        }, options);
      });
      window.addEventListener('scroll', () => this.queuePosition(), { ...options, passive: true });
      window.addEventListener('resize', () => {
        this.refresh();
        this.position();
        if (this.dialog?.open) this.selectZoom(this.zoomIndex, true);
      }, options);
      this.observer = new MutationObserver(() => this.queueRefresh());
      this.observer.observe(this.view, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'src', 'srcset'] });
      this.resizeObserver = new ResizeObserver(() => this.position());
      const info = root.querySelector('.product__info-container, .key-three__info');
      if (info) this.resizeObserver.observe(info);
      const header = document.querySelector('#shopify-section-header');
      if (header) this.resizeObserver.observe(header);
      if (this.dialog) this.setupZoom(options);
      this.refresh();
      this.position();
    }

    items() {
      const selector = this.root.hasAttribute('data-bs-standard-gallery')
        ? this.desktop.matches ? '.product__gallery.small-hide .product-media-item' : '.swiper-wrapper .product-media-item'
        : '.key-three__image';
      return [...this.view.querySelectorAll(selector)]
        .filter(item => !item.hidden && getComputedStyle(item).display !== 'none')
        .sort((a, b) => Number(getComputedStyle(a).order) - Number(getComputedStyle(b).order));
    }

    queueRefresh() {
      if (this.refreshFrame) return;
      this.refreshFrame = requestAnimationFrame(() => {
        this.refreshFrame = null;
        this.refresh();
      });
    }

    refresh() {
      this.visible = this.items();
      const modelNote = this.root.querySelector('[data-bs-model-note]');
      if (modelNote) {
        const firstImage = this.desktop.matches && this.visible.find(item => item.dataset.bsMediaType === 'image');
        if (firstImage && modelNote.parentElement !== firstImage) firstImage.append(modelNote);
        modelNote.hidden = !firstImage;
      }
      this.view.querySelectorAll('.product-media-item, .key-three__image').forEach((item, index) => {
        if (!this.labels.zoomEnabled || item.dataset.bsMediaType && item.dataset.bsMediaType !== 'image' || !item.querySelector('img')) return;
        if (item.querySelector('[data-bs-open]')) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'bs-gallery__open';
        button.dataset.bsOpen = '';
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-label', this.labels.open.replace('[index]', index + 1));
        item.append(button);
      });
      const fingerprint = this.visible.map(item => {
        const img = item.querySelector('img');
        return `${item.dataset.bsMediaId || item.dataset.galleryComponent + '-' + item.dataset.galleryImage}|${img?.getAttribute('src')}|${img?.getAttribute('srcset')}`;
      }).join(';');
      if (fingerprint !== this.fingerprint) {
        this.fingerprint = fingerprint;
        if (this.dialog?.open) this.dialog.close();
        this.nav.replaceChildren(...this.visible.map((item, index) => this.thumbnail(item, index, 'bsSelect')));
        this.nav.hidden = this.visible.length < 2;
      }
      this.visible.forEach((item, index) => item.querySelector('[data-bs-open]')?.setAttribute('aria-label', this.labels.open.replace('[index]', index + 1)));
      this.position();
    }

    thumbnail(item, index, action) {
      const image = item.querySelector('img');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bs-gallery__thumbnail';
      button.dataset[action] = String(index);
      button.setAttribute('aria-label', this.labels.load.replace('[index]', index + 1));
      button.setAttribute('aria-current', String(index === 0));
      const preview = document.createElement('img');
      preview.alt = '';
      preview.loading = 'lazy';
      preview.width = 44;
      preview.height = 54;
      if (image) {
        const url = new URL(image.getAttribute('src'), location.href);
        url.searchParams.set('width', '100');
        preview.src = url.href;
      }
      button.append(preview);
      return button;
    }

    click(event) {
      const thumbnail = event.target.closest('[data-bs-select]');
      if (thumbnail) {
        const item = this.visible[Number(thumbnail.dataset.bsSelect)];
        if (item) item.scrollIntoView({ block: 'start', behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
        return;
      }
      const opener = event.target.closest('[data-bs-open]');
      if (opener && !event.defaultPrevented) this.open(opener);
    }

    navigateThumbnails(event) {
      if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      const buttons = [...this.nav.querySelectorAll('button')];
      const index = buttons.indexOf(event.target);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
      event.preventDefault();
      buttons[next]?.focus();
      buttons[next]?.click();
    }

    queuePosition() {
      if (this.positionFrame) return;
      this.positionFrame = requestAnimationFrame(() => { this.positionFrame = null; this.position(); });
    }

    position() {
      const headerHeight = document.querySelector('#shopify-section-header')?.getBoundingClientRect().height || 80;
      const offset = headerHeight + 16;
      const info = this.root.querySelector('.product__info-container, .key-three__info');
      const infoHeight = info?.getBoundingClientRect().height || 0;
      this.root.style.setProperty('--bs-gallery-offset', `${offset}px`);
      this.root.style.setProperty('--bs-info-top', `${Math.min(offset, window.innerHeight - infoHeight - 24)}px`);
      if (!this.visible?.length) return;
      this.visible.forEach(item => {
        const button = item.querySelector('[data-bs-open]');
        if (!button) return;
        const rect = item.getBoundingClientRect();
        const centered = rect.left <= window.innerWidth / 2 && rect.right > window.innerWidth / 2;
        button.tabIndex = this.desktop.matches || centered ? 0 : -1;
      });
      const current = this.visible.findIndex(item => item.getBoundingClientRect().bottom > offset + 60);
      this.markCurrent(this.nav, Math.max(0, current));
    }

    markCurrent(nav, index) {
      [...nav.querySelectorAll('button')].forEach((button, i) => button.setAttribute('aria-current', String(i === index)));
      const button = nav.children[index];
      if (button && this.desktop.matches && (button.offsetTop < nav.scrollTop || button.offsetTop + button.offsetHeight > nav.scrollTop + nav.clientHeight)) {
        nav.scrollTop = Math.max(0, button.offsetTop - nav.clientHeight / 2);
      }
    }

    setupZoom(options) {
      this.zoomImages = this.dialog.querySelector('[data-bs-zoom-images]');
      this.zoomNav = this.dialog.querySelector('[data-bs-zoom-thumbnails]');
      this.dialog.querySelector('[data-bs-zoom-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.querySelector('[data-bs-zoom-in]').addEventListener('click', () => this.scaleTo(this.scale + .5), options);
      this.dialog.querySelector('[data-bs-zoom-out]').addEventListener('click', () => this.scaleTo(this.scale - .5), options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.previousOverflow || '';
        this.pointers?.clear();
        this.opener?.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.zoomItems.length - 1 : this.zoomIndex + (event.key === 'ArrowRight' ? 1 : -1);
        this.selectZoom(next);
      }, options);
      this.zoomNav.addEventListener('click', event => {
        const button = event.target.closest('[data-bs-zoom-select]');
        if (button) this.selectZoom(Number(button.dataset.bsZoomSelect));
      }, options);
      this.zoomImages.addEventListener('scroll', () => {
        if (!this.desktop.matches) return;
        const rect = this.zoomImages.getBoundingClientRect();
        const index = this.zoomSlides.findIndex(slide => slide.getBoundingClientRect().bottom > rect.top + rect.height / 3);
        if (index >= 0) { this.zoomIndex = index; this.markCurrent(this.zoomNav, index); }
      }, { ...options, passive: true });
      this.zoomImages.addEventListener('click', event => {
        if (event.target.tagName !== 'IMG') return;
        if (this.desktop.matches) this.dialog.close();
        else if (!this.dragged) this.scaleTo(this.scale > 1 ? 1 : 2);
      }, options);
      this.pointers = new Map();
      this.zoomImages.addEventListener('pointerdown', event => this.pointerDown(event), options);
      this.zoomImages.addEventListener('pointermove', event => this.pointerMove(event), options);
      for (const type of ['pointerup', 'pointercancel']) this.zoomImages.addEventListener(type, event => {
        this.pointers.delete(event.pointerId);
        this.gesture = null;
      }, options);
    }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener;
      this.zoomItems = this.visible.filter(item => item.dataset.bsMediaType !== 'video' && item.querySelector('img'));
      const selected = opener.closest('.product-media-item, .key-three__image');
      const index = this.zoomItems.indexOf(selected);
      if (index < 0) return;
      this.zoomSlides = this.zoomItems.map((item, i) => {
        const source = item.querySelector('img');
        const slide = document.createElement('div');
        slide.className = 'bs-zoom__slide';
        const image = document.createElement('img');
        const url = new URL(item.dataset.bg || source.getAttribute('src'), location.href);
        url.searchParams.set('width', '4096');
        image.src = url.href;
        image.alt = source.dataset.alt || source.alt.split(' | ').pop();
        image.loading = i === index ? 'eager' : 'lazy';
        image.width = Number(source.getAttribute('width')) || source.naturalWidth || 1200;
        image.height = Number(source.getAttribute('height')) || source.naturalHeight || 1500;
        image.addEventListener('error', () => { image.src = source.getAttribute('src'); }, { once: true });
        image.draggable = false;
        slide.append(image);
        return slide;
      });
      this.zoomImages.replaceChildren(...this.zoomSlides);
      this.zoomNav.replaceChildren(...this.zoomItems.map((item, i) => this.thumbnail(item, i, 'bsZoomSelect')));
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
      this.selectZoom(index, true);
    }

    selectZoom(index, instant = false) {
      if (!this.zoomSlides.length) return;
      this.zoomIndex = (index + this.zoomSlides.length) % this.zoomSlides.length;
      this.scale = this.desktop.matches ? 1 : 1.5;
      this.pan = { x: 0, y: 0 };
      this.pointers.clear();
      this.gesture = null;
      this.applyTransform();
      const target = this.zoomSlides[this.zoomIndex];
      const behavior = instant || this.reducedMotion.matches ? 'instant' : 'smooth';
      if (this.desktop.matches) this.zoomImages.scrollTo({ top: target.offsetTop, left: 0, behavior });
      else this.zoomImages.scrollTo({ left: target.offsetLeft, top: 0, behavior });
      this.markCurrent(this.zoomNav, this.zoomIndex);
    }

    scaleTo(scale) {
      this.scale = Math.max(1, Math.min(5, scale));
      this.applyTransform();
    }

    applyTransform() {
      const slide = this.zoomSlides[this.zoomIndex];
      const image = slide.querySelector('img');
      if (!slide.clientWidth || !slide.clientHeight) return;
      const fit = Math.min(slide.clientWidth / (image.naturalWidth || slide.clientWidth), slide.clientHeight / (image.naturalHeight || slide.clientHeight));
      const width = (image.naturalWidth || slide.clientWidth) * fit;
      const height = (image.naturalHeight || slide.clientHeight) * fit;
      const maxX = Math.max(0, (width * this.scale - slide.clientWidth) / 2);
      const maxY = Math.max(0, (height * this.scale - slide.clientHeight) / 2);
      this.pan.x = Math.max(-maxX, Math.min(maxX, this.pan.x));
      this.pan.y = Math.max(-maxY, Math.min(maxY, this.pan.y));
      slide.style.setProperty('--bs-zoom-scale', this.scale);
      slide.style.setProperty('--bs-pan-x', `${this.pan.x}px`);
      slide.style.setProperty('--bs-pan-y', `${this.pan.y}px`);
      this.dialog.querySelector('[data-bs-zoom-out]').disabled = this.scale <= 1;
      this.dialog.querySelector('[data-bs-zoom-in]').disabled = this.scale >= 5;
    }

    pointerDown(event) {
      if (this.desktop.matches || !event.target.closest('.bs-zoom__slide')) return;
      this.dragged = false;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      event.target.closest('.bs-zoom__slide').setPointerCapture(event.pointerId);
      this.startGesture();
    }

    startGesture() {
      const points = [...this.pointers.values()];
      if (!points.length) return;
      this.gesture = { points, pan: { ...this.pan }, scale: this.scale };
    }

    pointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (!this.gesture) this.startGesture();
      const points = [...this.pointers.values()];
      if (points.length !== this.gesture.points.length) this.startGesture();
      const initial = this.gesture.points;
      if (points.length === 2) {
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        const original = Math.hypot(initial[0].x - initial[1].x, initial[0].y - initial[1].y);
        if (original) this.scale = Math.max(1, Math.min(5, this.gesture.scale * distance / original));
        this.dragged = true;
      } else {
        const x = points[0].x - initial[0].x;
        const y = points[0].y - initial[0].y;
        if (Math.hypot(x, y) > 8) this.dragged = true;
        this.pan = { x: this.gesture.pan.x + x, y: this.gesture.pan.y + y };
      }
      this.applyTransform();
    }

    destroy() {
      if (this.dialog?.open) this.dialog.close();
      this.controller.abort();
      this.observer.disconnect();
      this.resizeObserver.disconnect();
      cancelAnimationFrame(this.refreshFrame);
      cancelAnimationFrame(this.positionFrame);
    }
  }

  const galleries = new Map();
  const initialize = container => container.querySelectorAll('[data-bs-gallery]').forEach(root => {
    if (!galleries.has(root)) galleries.set(root, new BleusaltProductGallery(root));
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => galleries.forEach((gallery, root) => {
    if (event.target.contains(root)) { gallery.destroy(); galleries.delete(root); }
  }));
})();
